import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import { useFeature } from 'flagged';
import CampaignSelect from '@/components/common/form/CampaignSelect';
import ConstituentAutocomplete from '@/components/common/form/ConstituentAutocomplete';
import ControlledMinMaxDonation from '@/components/common/form/ControlledMinMaxDonation';
import ControlledMinMaxHours from '@/components/common/form/ControlledMinMaxHours';
import ControlledTextField from '@/components/common/form/ControlledTextField';
import DonationTypeSelect from '@/components/common/form/DonationTypeSelect';
import LabelSelect from '@/components/common/form/LabelSelect';
import USAStateSelect from '@/components/common/USAStateSelect';
import ConstituentTypeSelect from '@/components/filters/ConstituentTypeSelect';
import ConnectionStatus from '@/constants/ConnectionStatus';
import ConstituentSearchFilterField from '@/constants/ConstituentSearchFilterField';
import FeatureFlags from '@/constants/system/FeatureFlags';
import translate from '@/lib/intl/translate';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { ConstituentAssigneeSelect } from '@/modules/ConstituentDetails';
import {
  ConstituentBirthdateFilters,
  ConstituentCategorySelect,
  ConstituentTimePeriod,
} from '@/modules/ConstituentListView';
import CustomFieldFilters from '@/modules/CustomFieldFilters';
import {
  IWaveSearchFilters,
  useIWaveIntegration,
} from '@/modules/IWaveIntegration';
import { useQueryProvider } from '@/modules/QueryProvider';
import DateTypeOptions from '../constants/DateTypeOptions';
import TemporaryFilterField from '../constants/TemporaryFilterField';
import useConstituentSearchFilter from '../hooks/useConstituentSearchFilter';
import type ConstituentSearchFilterParams from '../types/ConstituentSearchFilterParams';
import SavedFiltersSelect from './SavedFiltersSelect';

type ConstituentSearchFilterProps = {
  enableSavedFilters?: boolean;
  onClose: () => void;
  toggleAdvancedFilters: () => void;
};

const ConstituentSearchFilter = React.forwardRef<
  HTMLDivElement,
  ConstituentSearchFilterProps
>(({ enableSavedFilters = false, onClose, toggleAdvancedFilters }, ref) => {
  const hasIWave = useFeature(FeatureFlags.IWAVE);
  const iWaveIntegration = useIWaveIntegration();
  const { updateFilters } = useConstituentSearchFilter();
  const { control, handleSubmit, reset } = useFormContext();
  const { clearQuery } = useQueryProvider<ConstituentSearchFilterParams>();

  const [showMoreConstituentFilters, setShowMoreConstituentFilters] =
    React.useState(false);
  const [showMoreContributionFilters, setShowMoreContributionFilters] =
    React.useState(false);

  const handleClose = React.useCallback(() => {
    onClose();
    reset();
  }, [onClose]);

  const onSubmit = React.useCallback(
    (e: React.FormEvent) =>
      handleSubmit(() => {
        updateFilters();
        handleClose();
      })(e),
    [handleClose, handleSubmit],
  );

  return (
    <div
      ref={ref}
      className="absolute inset-0 flex flex-1 flex-col overflow-y-auto"
    >
      <form
        onSubmit={onSubmit}
        className="flex flex-1 flex-col overflow-y-auto"
      >
        <div className="flex-grow overflow-y-auto p-6">
          <div className="grid grid-cols-1 gap-4">
            {enableSavedFilters && (
              <SavedFiltersSelect onChange={handleClose} />
            )}
            <h3 className="type-body2">
              {translate('CONSTITUENT_INFORMATION')}
            </h3>
            <Controller
              control={control}
              name={ConstituentSearchFilterField.CONSTITUENT_TYPE}
              render={({ field: { onChange, value } }) => (
                <ConstituentTypeSelect value={value} onChange={onChange} />
              )}
            />
            <Controller
              control={control}
              name={ConstituentSearchFilterField.CONSTITUENT_CATEGORIES}
              render={({ field: { onChange, value } }) => (
                <ConstituentCategorySelect value={value} onChange={onChange} />
              )}
            />
            <Controller
              control={control}
              name={TemporaryFilterField.ASSIGNEE}
              render={({ field: { onChange, value } }) => (
                <ConstituentAssigneeSelect onChange={onChange} value={value} />
              )}
            />
            <Collapse in={showMoreConstituentFilters}>
              <div className="grid grid-cols-1 gap-4">
                <ControlledTextField
                  className="w-full sm:flex-1"
                  control={control}
                  id="constituent-filter-city"
                  InputLabelProps={{
                    htmlFor: 'constituent-filter-city',
                  }}
                  label={translate('FORMS_CITY_LABEL')}
                  name={ConstituentSearchFilterField.CITY}
                  size="small"
                />
                <div className="md:flex">
                  <Controller
                    control={control}
                    name={ConstituentSearchFilterField.STATE}
                    render={({ field: { onChange, value } }) => (
                      <USAStateSelect
                        className="mb-4 block w-full md:mr-2 md:mb-0 md:w-1/2 md:flex-1"
                        onChange={onChange}
                        value={value}
                      />
                    )}
                  />
                  <ControlledTextField
                    className="w-full md:ml-2 md:w-1/2 md:flex-1"
                    control={control}
                    id="constituent-filter-postal-code"
                    InputLabelProps={{
                      htmlFor: 'constituent-filter-postal-code',
                    }}
                    label={translate('FORMS_POSTAL_CODE_LABEL')}
                    name={ConstituentSearchFilterField.POSTAL_CODE}
                    size="small"
                  />
                </div>
                <ConstituentAutocomplete
                  label={translate('CONNECTED_TO')}
                  name={TemporaryFilterField.CONNECTION_CONSTITUENT}
                />
                <ConstituentBirthdateFilters
                  monthFieldName={ConstituentSearchFilterField.BIRTHDAY_MONTH}
                  bornAfterFieldName={`${TemporaryFilterField.BORN_DATE_RANGE}.0`}
                  bornBeforeFieldName={`${TemporaryFilterField.BORN_DATE_RANGE}.1`}
                />
                <Controller
                  control={control}
                  name={TemporaryFilterField.CONSTITUENT_LABELS}
                  render={({ field: { onChange, value } }) => (
                    <LabelSelect
                      value={value}
                      onChange={onChange}
                      disableCloseOnSelect
                    />
                  )}
                />
                <CustomFieldFilters
                  fieldPrefix={`${TemporaryFilterField.CUSTOM_FIELDS}.`}
                  fieldSuffix=".value"
                  allowShowMore={false}
                  hasAdvancedSearch
                />
              </div>
            </Collapse>
            <Button
              className="justify-start py-0 pl-1"
              onClick={() =>
                setShowMoreConstituentFilters(!showMoreConstituentFilters)
              }
              variant="text"
            >
              {showMoreConstituentFilters
                ? translate('SHOW_LESS')
                : translate('SHOW_MORE')}
            </Button>
            <h3 className="type-body2">
              {translate('CONTRIBUTION_INFORMATION')}
            </h3>
            <Controller
              control={control}
              name={ConstituentSearchFilterField.DONATION_TYPES}
              render={({ field: { onChange, value } }) => (
                <DonationTypeSelect onChange={onChange} value={value} />
              )}
            />
            <ConstituentTimePeriod
              dateTypeName={ConstituentSearchFilterField.DONATION_DATE_TYPE}
              options={DateTypeOptions}
              startDateName={`${TemporaryFilterField.DONATION_DATE_RANGE}.0`}
              endDateName={`${TemporaryFilterField.DONATION_DATE_RANGE}.1`}
            />
            <Controller
              control={control}
              name={ConstituentSearchFilterField.CAMPAIGN_IDS}
              render={({ field: { value, onChange } }) => (
                <CampaignSelect multiple onChange={onChange} value={value} />
              )}
            />
            <Collapse in={showMoreContributionFilters}>
              <div className="grid grid-cols-1 gap-4">
                <ControlledMinMaxDonation
                  minName={`${TemporaryFilterField.DONATION_AMOUNT_RANGE}.0`}
                  maxName={`${TemporaryFilterField.DONATION_AMOUNT_RANGE}.1`}
                />
                <ControlledMinMaxHours
                  minName={`${TemporaryFilterField.DONATION_HOURS_RANGE}.0`}
                  maxName={`${TemporaryFilterField.DONATION_HOURS_RANGE}.1`}
                />
                <Controller
                  control={control}
                  name={TemporaryFilterField.CONTRIBUTION_LABELS}
                  render={({ field: { onChange, value } }) => (
                    <LabelSelect
                      value={value}
                      onChange={onChange}
                      disableCloseOnSelect
                    />
                  )}
                />
              </div>
            </Collapse>
            <Button
              className="justify-start py-0 pl-1"
              onClick={() =>
                setShowMoreContributionFilters(!showMoreContributionFilters)
              }
              variant="text"
            >
              {showMoreContributionFilters
                ? translate('SHOW_LESS')
                : translate('SHOW_MORE')}
            </Button>

            {hasIWave &&
              iWaveIntegration?.status === ConnectionStatus.CONNECTED && (
                <IWaveSearchFilters />
              )}
          </div>
        </div>

        <div className="flex-0 z-10 border-t border-grey-300 px-6 pt-4 pb-6">
          <div className="text-right">
            <Button
              className="mb-6 p-0 text-text-secondary type-h2 hover:text-primary-main"
              onClick={toggleAdvancedFilters}
              type="button"
              endIcon={<ChevronRight />}
              classes={{
                endIcon: 'ml-0',
              }}
            >
              {translate('ADVANCED_FILTERS')}
            </Button>
          </div>
          <div className="flex flex-row-reverse">
            <Button
              variant="contained"
              type="submit"
              data-testid="filter-apply-button"
            >
              {translate('APPLY')}
            </Button>
            <Button
              onClick={() => {
                clearQuery();
                onClose();
              }}
            >
              {translate('CLEAR_ALL')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
});

ConstituentSearchFilter.displayName = 'ConstituentSearchFilter';

export default ConstituentSearchFilter;
